import React, { useState, useEffect } from 'react';
import {
  Users,
  User,
  MapPin,
  Edit2,
  Trash2,
  Plus,
  Save,
  X,
  Mail,
  Globe,
  Award,
  Briefcase,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  XIcon
} from 'lucide-react';
import './EditorialBoard.css';
import { ApiUrl } from '../API/ApiUrl';
import Swal from 'sweetalert2';

const EditorialBoard = () => {
  const [formData, setFormData] = useState({
    editorType: '',
    name: '',
    location: '',
    affiliation: '',
    email: '',
    qualification: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const itemsPerPage = 10;

  const [editorialBoard, setEditorialBoard] = useState([]);

  const editorTypes = [
    'Executive Editors',
    // 'Associate Editor',
    'National Scientific Board Member',
    // 'Editor in chief',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errorMessage) setErrorMessage('');
  };

  const nameRegex = /^[A-Za-z\s.]+$/;
  const locationRegex = /^[A-Za-z\s,.-]+$/;
  const qualificationRegex = /^[A-Za-z\s.,-]+$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|in)$/;

  const validateForm = () => {
    if (!formData.editorType) {
      setErrorMessage('Please select editor type');
      return false;
    }

    if (!formData.name.trim()) {
      setErrorMessage('Please enter editor name');
      return false;
    } else if (!nameRegex.test(formData.name)) {
      setErrorMessage('Name should contain only letters');
      return false;
    }

    //  OPTIONAL FIELD (validate only if entered)
    if (formData.qualification.trim() && !qualificationRegex.test(formData.qualification)) {
      setErrorMessage('Invalid qualification format');
      return false;
    }

    if (!formData.location.trim()) {
      setErrorMessage('Please enter location');
      return false;
    } else if (!locationRegex.test(formData.location)) {
      setErrorMessage('Invalid location format');
      return false;
    }

    //  OPTIONAL EMAIL (validate only if entered)
    if (formData.email.trim() && !emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const fetchEditors = async () => {
    // const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${ApiUrl}/editorial-board`);
      const data = await response.json();


      setEditorialBoard(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEditors();
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const token = localStorage.getItem("adminToken");

    const payload = {
      editor_type: formData.editorType,   // mapped
      name: formData.name,
      qualification: formData.qualification,
      location: formData.location,
      email: formData.email,
      status: "active"
    };
    setSaving(true)
    try {
      let response;

      if (editingId) {
        // UPDATE
        response = await fetch(`${ApiUrl}/editorial-board/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,  // ✅ Bearer token added
          },
          body: JSON.stringify(payload),
        });

      } else {
        // CREATE
        response = await fetch(`${ApiUrl}/editorial-board`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,  // ✅ Bearer token added
          },
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }

      setSuccessMessage(data.message);
      fetchEditors(); // refresh from DB
      resetForm();

      setTimeout(() => setSuccessMessage(""), 3000);

    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false)

    }
  };

  const handleEdit = (editor) => {
    setFormData({
      editorType: editor.editor_type,  // mapped
      name: editor.name,
      location: editor.location,
      email: editor.email,
      qualification: editor.qualification || ""
    });

    setEditingId(editor.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this editor!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    });

    if (!result.isConfirmed) return;

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${ApiUrl}/editorial-board/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }
      await Swal.fire({
        title: "Deleted!",
        text: data.message,
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });

      
      fetchEditors();


    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text: "Something went wrong",
        icon: "error"
      });
    }
  };
  const resetForm = () => {
    setFormData({
      editorType: '',
      name: '',
      location: '',
      //   affiliation: '',
      email: '',
      qualification: ''
    });
    setEditingId(null);
    setShowForm(false);
    setErrorMessage('');
  };

  // Filter editors based on search
  const filteredEditors = editorialBoard.filter(editor =>
    editor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    editor.editor_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    editor.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // editor.affiliation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    editor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredEditors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEditors = filteredEditors.slice(startIndex, startIndex + itemsPerPage);

  // Get counts by editor type
  // Get counts by editor type
  const editorTypeCounts = editorialBoard.reduce((acc, editor) => {
    const type = editor.editor_type;  // ✅ fixed

    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});



  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages]);

  return (
    <div className="editorial-board-page">
      {/* Header */}
      <div className="page-header">
        <div className="title-section">
          <Users size={40} className="title-icon" />
          <div>
            <h1 className="page-title">Editorial Board</h1>
            <p className="page-subtitle">Manage editorial board members and their information</p>
          </div>
        </div>

        {!showForm && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={16} />
            <span>Add Editor</span>
          </button>
        )}
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert success">
          <CheckCircle size={16} />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="alert error">
          <AlertCircle size={16} />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Stats Cards */}
      {/* {filteredEditors.length !== 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Users size={16} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Total Editors</span>
              <span className="stat-value">{editorialBoard.length}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Award size={16} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Editor-in-Chief</span>
              <span className="stat-value">{editorTypeCounts['Editor in chief'] || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Briefcase size={16} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Associate Editors</span>
              <span className="stat-value">{editorTypeCounts['Associate Editor'] || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Globe size={16} />
            </div>
            <div className="stat-info">
              <span className="stat-label">Countries</span>
              <span className="stat-value">{new Set(editorialBoard.map(e => e.location)).size}</span>
            </div>
          </div>
        </div>
      )} */}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-wrapper">
          <Search size={16} className="search-icon" />

          <input
            type="text"
            placeholder="Search by name, type, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          {searchTerm && (
            <XIcon
              size={16}
              className="clear-icon"
              onClick={() => setSearchTerm("")}
            />
          )}
        </div>
        {/* <button className="filter-btn">
          <Filter size={16} />
          <span>Filter</span>
        </button> */}
      </div>

      {/* Form Section */}
      {showForm && (
        <div className="form-card">
          <div className="form-header">
            <h3>{editingId ? 'Edit Editor' : 'Add New Editor'}</h3>
            <button className="btn-icon" onClick={resetForm}>
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="editorType">
                  <Award size={14} />
                  <span>Editor Type <span className="required">*</span></span>
                </label>
                <select
                  id="editorType"
                  name="editorType"
                  value={formData.editorType}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">Select type</option>
                  {editorTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="name">
                  <User size={14} />
                  <span>Full Name <span className="required">*</span></span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="location">
                  <MapPin size={14} />
                  <span>Location <span className="required">*</span></span>
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Country / City"
                  className="form-input"
                  required
                />
              </div>

              {/* <div className="form-group">
                <label htmlFor="affiliation">
                  <Briefcase size={14} />
                  <span>Affiliation</span>
                </label>
                <input
                  type="text"
                  id="affiliation"
                  name="affiliation"
                  value={formData.affiliation}
                  onChange={handleInputChange}
                  placeholder="University / Institution"
                  className="form-input"
                />
              </div> */}
              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={14} />
                  <span>Email </span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="form-input"
                // required
                />
              </div>
            </div>

            <div className="form-row">


              <div className="form-group">
                <label htmlFor="qualification">
                  <Award size={14} />
                  <span>Qualification</span>
                </label>
                <input
                  type="text"
                  id="qualification"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleInputChange}
                  placeholder="Qualification"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {/* <Save size={14} /> */}
                {/* <span>{editingId ? 'Update Editor' : 'Save Editor'}</span> */}

                {saving ? (
                  <>
                    <Loader2 size={16} className="spin-icon" />
                    <span>{editingId ? 'Updating...' : 'Saving...'}</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>{editingId ? 'Update Editor' : 'Save Editor'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <h3>Editorial Board Members</h3>
          <span className="record-count">{filteredEditors.length} members</span>
        </div>

        <div className="table-responsive">
          <table className="editorial-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Editor Type</th>
                <th>Name</th>
                <th>Qualification</th>

                <th>Location</th>
                {/* <th>Affiliation</th> */}
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEditors.map((editor, index) => (
                <tr key={editor.id}>
                  <td>
                    <span className="sl-no">{startIndex + index + 1}</span>
                  </td>
                  <td>
                    <span className="editor-type-badge">
                      {editor.editor_type}
                    </span>
                  </td>
                  <td>
                    <div className="name-wrapper">
                      <User size={12} className="name-icon" />
                      <span className="editor-name">{editor.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="affiliation-wrapper">
                      <Briefcase size={12} className="affiliation-icon" />
                      <span className="affiliation-text">{editor.qualification || '-'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="location-wrapper">
                      <MapPin size={12} className="location-icon" />
                      <span>{editor.location}</span>
                    </div>
                  </td>
                  {/* <td>
                    <div className="affiliation-wrapper">
                      <Briefcase size={12} className="affiliation-icon" />
                      <span className="affiliation-text">{editor.affiliation || '-'}</span>
                    </div>
                  </td> */}

                  <td>
                    <div className="email-wrapper">
                      <Mail size={12} className="email-icon" />
                      <span className="email-text">{editor.email || '-'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEdit(editor)}
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(editor.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredEditors.length === 0 && (
            <div className="empty-state">
              <Users size={40} />
              <p>No editors found</p>

            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredEditors.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>

            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Quick Stats Footer */}
      <div className="notifications-footer">
        <div className="footer-stat">
          <Users size={14} />
          <span>Total Editors: {editorialBoard.length}</span>
        </div>
        <div className="footer-stat">
          <Globe size={14} />
          <span>Countries: {new Set(editorialBoard.map(e => e.location)).size}</span>
        </div>
        {/* <div className="footer-stat">
          <Award size={14} />
          <span>Editor-in-Chief: {editorTypeCounts['Editor in chief'] || 0}</span>
        </div>
        <div className="footer-stat">
          <Briefcase size={14} />
          <span>Associate Editors: {editorTypeCounts['Associate Editor'] || 0}</span>
        </div> */}
      </div>
    </div>
  );
};

export default EditorialBoard;